import type React from "react"
import { type RefObject, useCallback, useEffect, useRef } from "react"

import { getVerticalScrollOverflow } from "../utils/vertical-scroll-overflow.ts"

interface UseInfiniteScrollParams<T extends HTMLElement> {
  loading: boolean
  hasMore: boolean
  disabled?: boolean
  loadMore: () => void | Promise<void>
  scrollThreshold?: number
  /**
   * Identity of the page that would load next — the `next` URL. Both triggers
   * below can fire for the same page, and `loading` only catches up a render
   * later, so this is what tells the second call it is a duplicate. Left unset,
   * the guard is off and a page can be requested and appended twice.
   */
  loadMoreKey?: string | null
  /**
   * Element containing the table, when the caller already holds a ref to it —
   * e.g. because it also measures the body's height from there.
   */
  containerRef?: RefObject<T | null>
}

export const useInfiniteScroll = <T extends HTMLElement = HTMLDivElement>({
  loading,
  hasMore,
  disabled = false,
  loadMore,
  scrollThreshold = 50,
  loadMoreKey = null,
  containerRef
}: UseInfiniteScrollParams<T>) => {
  const loadingRef = useRef(false)
  const inFlightKey = useRef<string | null>(null)
  // The fallback below has to find *this* table's body, and
  // `document.querySelector` finds the first one in the document — another
  // table's, as soon as a view renders two. So it searches within the container,
  // which the caller must attach for the fallback to work at all.
  const ownRef = useRef<T>(null)
  const container = containerRef ?? ownRef

  useEffect(() => {
    // The store settling the request is what frees the key. Timing it off
    // `loadMore` instead frees it a microtask after the call, because most
    // callers dispatch and return nothing to wait on.
    if (loadingRef.current && !loading) inFlightKey.current = null
    loadingRef.current = loading
  }, [loading])

  const requestMore = useCallback(async () => {
    if (loadMoreKey !== null && inFlightKey.current === loadMoreKey) return
    inFlightKey.current = loadMoreKey

    try {
      await loadMore()
    } catch {
      // The caller owns the failure — its reducer decides what a failed page
      // does to the rows on screen. Rethrowing would only surface as an
      // unhandled rejection, and a caller that threw never moved `loading`, so
      // this is the one path the transition above cannot free.
      inFlightKey.current = null
    }
  }, [loadMore, loadMoreKey])

  const onTableScroll: React.UIEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (disabled || loadingRef.current || !hasMore) return

      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
      if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
        void requestMore()
      }
    },
    [disabled, hasMore, scrollThreshold, requestMore]
  )

  // Fallback: the content fits, so scrolling can never ask for the next page.
  useEffect(() => {
    if (disabled || loading || !hasMore) return

    const timer = setTimeout(() => {
      const body =
        container.current?.querySelector<HTMLElement>("div.ant-table-body")
      if (!body) return

      // Fill until there is enough real vertical overflow for the scroll
      // listener to become reachable. Horizontal scrollbar chrome is not row
      // overflow and must not strand a short first batch on screen.
      if (getVerticalScrollOverflow(body) < scrollThreshold) {
        void requestMore()
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [disabled, loading, hasMore, requestMore, container])

  return { onTableScroll, containerRef: container }
}
